import React, { useEffect, useState } from "react";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Assessment,
  CalendarToday,
  Fastfood,
  LocalPizza,
  Restaurant,
  Schedule,
  StoreMallDirectory,
  TrendingUp,
} from "@mui/icons-material";
import { foodTypeTimePattern } from "../../../../api/product";

const FoodTypeTimePatternAnalysis = ({ startDate, endDate }) => {
  const theme = useTheme();
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const weekdayNames = [
    "Segunda",
    "Terça",
    "Quarta",
    "Quinta",
    "Sexta",
    "Sábado",
    "Domingo",
  ];

  const complexityColors = {
    simple: {
      bg: theme.palette.success.light,
      text: theme.palette.success.dark,
    },
    medium: {
      bg: theme.palette.warning.light,
      text: theme.palette.warning.dark,
    },
    complex: { bg: theme.palette.error.light, text: theme.palette.error.dark },
  };

  const complexityLabels = {
    simple: "Simples",
    medium: "Médio",
    complex: "Complexo",
  };

  const getFoodTypeColor = (index) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
    ];
    return colors[index % colors.length];
  };

  const getFoodTypeIcon = (foodTypeIcon) => {
    // Map common food type icons to Material-UI icons
    const iconMap = {
      LocalPizzaIcon: LocalPizza,
      FastfoodIcon: Fastfood,
      RestaurantIcon: Restaurant,
      default: Restaurant,
    };

    return iconMap[foodTypeIcon] || iconMap.default;
  };

  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
        });

        const response = await foodTypeTimePattern(params);
        const data = await response.data;
        setAnalysisData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisData();
  }, [startDate, endDate]);

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getFoodTypeDisplayName = (foodTypeIcon) => {
    if (!foodTypeIcon) return "Restaurante";
    return foodTypeIcon
      .replace("Icon", "")
      .replace(/([A-Z])/g, " $1")
      .trim();
  };

  const renderSummaryStats = () => {
    if (!analysisData) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ backgroundColor: theme.palette.primary.main }}>
                <Restaurant />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Pedidos Analisados
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  {analysisData.totalOrdersAnalyzed}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.success.main,
                0.1
              )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ backgroundColor: theme.palette.success.main }}>
                <Fastfood />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Tipos de Comida
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {analysisData.uniqueFoodTypesAnalyzed}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.info.main,
                0.1
              )} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ backgroundColor: theme.palette.info.main }}>
                <StoreMallDirectory />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Período de Análise
                </Typography>
                <Typography variant="body2" fontWeight={600} color="info.main">
                  {analysisData.dateRange?.startDate &&
                    new Date(
                      analysisData.dateRange.startDate
                    ).toLocaleDateString("pt-BR")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  até{" "}
                  {analysisData.dateRange?.endDate &&
                    new Date(analysisData.dateRange.endDate).toLocaleDateString(
                      "pt-BR"
                    )}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.warning.main,
                0.1
              )} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ backgroundColor: theme.palette.warning.main }}>
                <Assessment />
              </Avatar>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Fuso Horário
                </Typography>
                <Typography variant="h6" fontWeight={700} color="warning.main">
                  SP
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {analysisData.timezone}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderComplexityAnalysis = () => {
    if (!analysisData?.orderComplexityAnalysis) return null;

    return (
      <Box>
        <Typography variant="h6" fontWeight={600} mb={3} color="text.primary">
          Análise de Complexidade dos Pedidos por Tipo de Comida
        </Typography>

        <Grid container spacing={3}>
          {Object.entries(analysisData.orderComplexityAnalysis).map(
            ([complexity, data]) => (
              <Grid item xs={12} md={4} key={complexity}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      complexityColors[complexity].bg,
                      0.1
                    )} 0%, ${alpha(
                      complexityColors[complexity].bg,
                      0.05
                    )} 100%)`,
                    border: `1px solid ${alpha(
                      complexityColors[complexity].bg,
                      0.3
                    )}`,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Chip
                        label={complexityLabels[complexity]}
                        sx={{
                          backgroundColor: complexityColors[complexity].bg,
                          color: complexityColors[complexity].text,
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {data.orderCount} pedidos
                      </Typography>
                    </Stack>

                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Tempo Médio:
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatTime(data.averageTime)}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Variação:
                        </Typography>
                        <Typography variant="body2">
                          {formatTime(data.minTime)} -{" "}
                          {formatTime(data.maxTime)}
                        </Typography>
                      </Stack>

                      <Box
                        sx={{
                          backgroundColor: alpha(theme.palette.grey[300], 0.3),
                          borderRadius: 1,
                          height: 6,
                        }}
                      >
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 1,
                            backgroundColor: complexityColors[complexity].bg,
                            width: `${Math.min(
                              (data.averageTime / 60) * 100,
                              100
                            )}%`,
                          }}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            )
          )}
        </Grid>
      </Box>
    );
  };

  const renderHourlyFoodTypePattern = (hourPattern) => {
    const foodTypePatterns = hourPattern.foodTypePatterns || {};

    if (Object.keys(foodTypePatterns).length === 0) {
      return null;
    }

    const hasData = Object.keys(foodTypePatterns).length > 0;
    const topFoodTypes = Object.entries(foodTypePatterns)
      .sort(([, a], [, b]) => b.averageKitchenTime - a.averageKitchenTime)
      .slice(0, 3);

    return (
      <Paper
        key={`hour-${hourPattern.hour}`}
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 3,
          backgroundColor: hasData
            ? alpha(theme.palette.primary.main, 0.02)
            : alpha(theme.palette.grey[300], 0.05),
          border: `1px solid ${
            hasData
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.grey[300], 0.2)
          }`,
          minHeight: 200,
          minWidth: 180,
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color={hasData ? "primary" : "text.secondary"}
          >
            {hourPattern.hour}:00
          </Typography>

          {hasData ? (
            <Stack spacing={1.5} width="100%">
              {topFoodTypes.map(([foodTypeIcon, data], index) => {
                const IconComponent = getFoodTypeIcon(foodTypeIcon);
                return (
                  <Box key={foodTypeIcon}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={0.5}
                    >
                      <Tooltip
                        title={getFoodTypeDisplayName(foodTypeIcon)}
                        arrow
                      >
                        <Chip
                          icon={<IconComponent />}
                          size="small"
                          sx={{
                            fontSize: "0.65rem",
                            height: 24,
                            backgroundColor: alpha(
                              getFoodTypeColor(index),
                              0.1
                            ),
                            color: getFoodTypeColor(index),
                            fontWeight: 600,
                            minWidth: 32,
                            "& .MuiChip-icon": {
                              fontSize: "1rem",
                              margin: 0,
                            },
                            "& .MuiChip-label": {
                              display: "none",
                            },
                          }}
                        />
                      </Tooltip>
                      <Typography variant="caption" fontWeight={600}>
                        {formatTime(data.averageKitchenTime)}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Pedidos: {data.orderCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Restaurantes: {data.uniqueRestaurants}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="center" mb={1}>
                      <Tooltip
                        title={`Média de ${data.averageProductsPerOrder} produtos por pedido`}
                        arrow
                      >
                        <Chip
                          label={`${data.averageProductsPerOrder} prod/pedido`}
                          size="small"
                          sx={{
                            fontSize: "0.6rem",
                            height: 16,
                            backgroundColor: alpha(
                              theme.palette.info.main,
                              0.1
                            ),
                            color: theme.palette.info.main,
                          }}
                        />
                      </Tooltip>
                    </Stack>

                    <Box
                      sx={{
                        height: 4,
                        backgroundColor: alpha(getFoodTypeColor(index), 0.1),
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${Math.min(
                            (data.averageKitchenTime / 60) * 100,
                            100
                          )}%`,
                          backgroundColor: getFoodTypeColor(index),
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
              {Object.keys(foodTypePatterns).length > 3 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  +{Object.keys(foodTypePatterns).length - 3} outros tipos
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Sem dados
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderWeeklyFoodTypePattern = (weekPattern) => {
    const foodTypePatterns = weekPattern.foodTypePatterns || {};
    const hasData = Object.keys(foodTypePatterns).length > 0;
    const topFoodTypes = Object.entries(foodTypePatterns)
      .sort(([, a], [, b]) => b.averageKitchenTime - a.averageKitchenTime)
      .slice(0, 4);

    return (
      <Paper
        key={`week-${weekPattern.weekday}`}
        elevation={2}
        sx={{
          p: 2.5,
          borderRadius: 3,
          backgroundColor: hasData
            ? alpha(theme.palette.secondary.main, 0.02)
            : alpha(theme.palette.grey[300], 0.05),
          border: `1px solid ${
            hasData
              ? alpha(theme.palette.secondary.main, 0.1)
              : alpha(theme.palette.grey[300], 0.2)
          }`,
          minHeight: 250,
        }}
      >
        <Stack spacing={2}>
          <Box textAlign="center">
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color={hasData ? "secondary.main" : "text.secondary"}
            >
              {weekdayNames[weekPattern.weekday]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {weekPattern.weekdayName}
            </Typography>
          </Box>

          {hasData ? (
            <Stack spacing={1.5}>
              {topFoodTypes.map(([foodTypeIcon, data], index) => {
                const IconComponent = getFoodTypeIcon(foodTypeIcon);
                return (
                  <Box key={foodTypeIcon}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip
                          title={getFoodTypeDisplayName(foodTypeIcon)}
                          arrow
                        >
                          <IconComponent
                            sx={{
                              fontSize: 20,
                              color: getFoodTypeColor(index),
                            }}
                          />
                        </Tooltip>
                      </Stack>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={getFoodTypeColor(index)}
                      >
                        {formatTime(data.averageKitchenTime)}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                      mb={1}
                    >
                      <Box textAlign="center" flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          Min
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                        >
                          {formatTime(data.minTime)}
                        </Typography>
                      </Box>
                      <Box textAlign="center" flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          Max
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                        >
                          {formatTime(data.maxTime)}
                        </Typography>
                      </Box>
                      <Box textAlign="center" flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          Rest.
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                        >
                          {data.uniqueRestaurants}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" justifyContent="center">
                      <Chip
                        label={`${data.orderCount} pedidos`}
                        size="small"
                        sx={{
                          fontSize: "0.6rem",
                          height: 16,
                          backgroundColor: alpha(getFoodTypeColor(index), 0.1),
                          color: getFoodTypeColor(index),
                        }}
                      />
                    </Stack>
                  </Box>
                );
              })}
              {Object.keys(foodTypePatterns).length > 4 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  +{Object.keys(foodTypePatterns).length - 4} outros tipos
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Sem dados para este dia
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderFoodTypeSummary = () => {
    if (
      !analysisData.foodTypeSummary ||
      analysisData.foodTypeSummary.length === 0
    ) {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            Nenhum tipo de comida encontrado no período selecionado
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}
            >
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Tipo de Comida
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Tempo Médio na Cozinha
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Pedidos / Restaurantes
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Contexto de Pedidos
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Restaurantes
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analysisData.foodTypeSummary.map((foodType, index) => {
              const IconComponent = getFoodTypeIcon(foodType.foodTypeIcon);
              return (
                <TableRow
                  key={foodType.foodTypeIcon}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: alpha(theme.palette.action.hover, 0.02),
                    },
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: alpha(getFoodTypeColor(index), 0.1),
                          color: getFoodTypeColor(index),
                          fontSize: "0.75rem",
                        }}
                      >
                        <IconComponent fontSize="small" />
                      </Avatar>
                      <Box>
                        <Tooltip
                          title={getFoodTypeDisplayName(foodType.foodTypeIcon)}
                          arrow
                        >
                          <Typography variant="body2" fontWeight={600}>
                            <IconComponent
                              sx={{
                                fontSize: 20,
                                verticalAlign: "middle",
                                mr: 1,
                              }}
                            />
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          {foodType.foodTypeIcon}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label={formatTime(foodType.averageKitchenTime)}
                      size="small"
                      sx={{
                        backgroundColor: alpha(getFoodTypeColor(index), 0.1),
                        color: getFoodTypeColor(index),
                        fontWeight: 600,
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {formatTime(foodType.minTime)} -{" "}
                      {formatTime(foodType.maxTime)}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>
                      {foodType.orderCount}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      {foodType.uniqueRestaurants} restaurantes
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>
                      {foodType.averageProductsPerOrder} produtos/pedido
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {foodType.averageQuantityPerOrder} itens/pedido
                    </Typography>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.5}
                      flexWrap="wrap"
                    >
                      {Object.entries(foodType.complexityBreakdown).map(
                        ([complexity, count]) =>
                          count > 0 && (
                            <Chip
                              key={complexity}
                              label={`${complexityLabels[complexity]}: ${count}`}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 18,
                                backgroundColor: alpha(
                                  complexityColors[complexity].bg,
                                  0.2
                                ),
                                color: complexityColors[complexity].text,
                                mb: 0.5,
                              }}
                            />
                          )
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>
                      {foodType.uniqueRestaurants}
                    </Typography>
                    <Box sx={{ mt: 1, maxHeight: 100, overflowY: "auto" }}>
                      {foodType.restaurantNames
                        ?.slice(0, 3)
                        .map((name, idx) => (
                          <Typography
                            key={idx}
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mb: 0.25 }}
                          >
                            {name?.length > 20
                              ? `${name.substring(0, 20)}...`
                              : name}
                          </Typography>
                        ))}
                      {foodType.restaurantNames?.length > 3 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          +{foodType.restaurantNames.length - 3} outros
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderLoadingState = () => (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<Fastfood />}
        title="Análise de Tempo por Tipo de Comida"
        sx={{ backgroundColor: theme.palette.secondary.main }}
        titleTypographyProps={{ sx: { color: "#ffffff" } }}
        sx={{ "& .MuiCardHeader-avatar": { color: "#ffffff" } }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress size={40} />
        </Box>
      </CardContent>
    </Card>
  );

  const renderErrorState = () => (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<Fastfood />}
        title="Análise de Tempo por Tipo de Comida"
        sx={{ backgroundColor: theme.palette.secondary.main }}
        titleTypographyProps={{ sx: { color: "#ffffff" } }}
        sx={{ "& .MuiCardHeader-avatar": { color: "#ffffff" } }}
      />
      <CardContent sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Erro ao carregar dados de tempo por tipo de comida: {error}
        </Alert>
      </CardContent>
    </Card>
  );

  if (loading) return renderLoadingState();
  if (error) return renderErrorState();
  if (!analysisData) return null;

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.secondary.main,
          0.02
        )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
            <Fastfood />
          </Avatar>
        }
        title={
          <Typography variant="h6" fontWeight={700}>
            Análise de Tempo por Tipo de Comida
          </Typography>
        }
        subheader={
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Análise detalhada de performance por categoria de restaurante
          </Typography>
        }
        sx={{
          backgroundColor: theme.palette.secondary.main,
          color: "white",
          "& .MuiCardHeader-avatar": { mr: 2 },
        }}
      />

      <CardContent sx={{ p: 3 }}>
        {renderSummaryStats()}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            aria-label="food type time pattern tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <Tab
              icon={<Schedule />}
              iconPosition="start"
              label="Padrões por Hora"
            />
            <Tab
              icon={<CalendarToday />}
              iconPosition="start"
              label="Padrões Semanais"
            />
            <Tab
              icon={<TrendingUp />}
              iconPosition="start"
              label="Resumo por Tipo"
            />
            <Tab
              icon={<Assessment />}
              iconPosition="start"
              label="Análise de Complexidade"
            />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              color="text.primary"
            >
              Tempo na Cozinha por Hora do Dia - Tipos de Comida
            </Typography>
            <Box
              sx={{
                overflowX: "auto",
                pb: 2,
                "&::-webkit-scrollbar": {
                  height: 8,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.5),
                  borderRadius: 4,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.7),
                  },
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ minWidth: "max-content", py: 1 }}
              >
                {analysisData.hourlyPatterns?.map(renderHourlyFoodTypePattern)}
              </Stack>
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              color="text.primary"
            >
              Tempo na Cozinha por Dia da Semana - Tipos de Comida
            </Typography>
            <Grid container spacing={2}>
              {analysisData.weeklyPatterns?.map((weekPattern) => (
                <Grid item xs={12} sm={6} md={4} key={weekPattern.weekday}>
                  {renderWeeklyFoodTypePattern(weekPattern)}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              color="text.primary"
            >
              Resumo Detalhado por Tipo de Comida
            </Typography>
            {renderFoodTypeSummary()}
          </Box>
        )}

        {tabValue === 3 && renderComplexityAnalysis()}
      </CardContent>
    </Card>
  );
};

export default FoodTypeTimePatternAnalysis;
